import { Grid } from "@mui/material";
import { ActionsCell } from "../../Component/Common/Components/DataTable/ActionsCell";
import { DefaultCell } from "../../Component/Common/Components/DataTable/DefaultCell";
import { VAExcelExportsHistory } from "../../Component/Model/Dto/VerificationAgency/VaExcelExports";
import { TableDateHelper } from "../../Component/Model/helpers";
import { EntriesPerPage, TableColumn } from "../../Component/Model/Table/Table";
import { DataTable } from "../../Component/Common/Components/DataTable/DataTable";
import React, { useEffect } from "react";
import { VAExcelExportService } from "../../Component/Api/VAExcelExport/VAExcelExportService";
import { VAExcelExportStatus } from "../../Component/Model/Enum/BEE123Enums";

export const VAExcelExportsHistoryTable = () => {
    const [entriesPerPage, setEntriesPerPage] = React.useState<EntriesPerPage>({ defaultValue: 5 });
    const [pageNumber, setPageNumber] = React.useState<number>(1);
    const [filteredItemsCount, setFilteredItemsCount] = React.useState<number>(0);
    const [totalItemsCount, setTotalItemsCount] = React.useState<number>(0);
    const [searchValue, setSearchValue] = React.useState<string>('');
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [historyData, setHistoryData] = React.useState<VAExcelExportsHistory.Dto[]>([])

    interface VAExcelExportRow {
        Id: number;
        FileName: string,
        CreatedDateUTC: string,
        CreatedBy: string
        Actions?: any;
    }
    
    interface VAExcelExportTable {
        columns: Array<TableColumn>;
        rows: Array<VAExcelExportRow>;
    }

	const entriesPerPageChanged = (entriesPerPage: number) => {
		setEntriesPerPage({ defaultValue: entriesPerPage });
		setPageNumber(1);
	};

    const pageNumberChanged = (pageNumber: number) => {
		setPageNumber(pageNumber);
	};

    const searchValueChanged = (searchValue: string) => {
		setSearchValue(searchValue);
	};

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const ExportSearch: VAExcelExportsHistory.SearchRequest = {
                PageNumber: pageNumber,
                SearchValue: searchValue,
                PageSize: entriesPerPage.defaultValue!,
            };
            const response = await VAExcelExportService.SearchVAExcelExports(ExportSearch);
            setHistoryData(response.data.results);
            setFilteredItemsCount(response.data.filteredItemsCount);
            setTotalItemsCount(response.data.totalItemsCount);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = async (id: number) => {
        try {
            const response = await VAExcelExportService.DownloadVAExcelExport(id);
            window.open(response.data);
        } catch (error: any) {
            if (error.response != null) {
                console.error(error.response.data);
            }
        }
    };

    useEffect(() => {
        fetchData();
    }, [entriesPerPage, pageNumber, searchValue]); // eslint-disable-line react-hooks/exhaustive-deps
    
    function VAExcelExportsHistoryTableMapper(dataObject: VAExcelExportsHistory.Dto[], handleDownload: (id: number) => void): VAExcelExportTable {
        const columns = [
            { Header: 'Created Date', accessor: 'CreatedDateUTC', Cell: ({ value }: any) => <DefaultCell value={value} /> },
            { Header: 'Created By', accessor: 'CreatedBy', Cell: ({ value }: any) => <DefaultCell value={value} /> },
            { Header: 'File Name', accessor: 'FileName', Cell: ({ value }: any) => <DefaultCell value={value} /> },
            { Header: 'Status', accessor: 'Status', Cell: ({ value }: any) => <DefaultCell value={value} />},
            { Header: 'Scorecard Name', accessor: 'ScorecardName', Cell: ({ value }: any) => <DefaultCell value={value} />},
            { Header: 'Actions', accessor: 'Actions', Cell: ({ value }: any) => <ActionsCell actions={value} />, disableSortBy: true }
        ];
    
        if (dataObject.length > 0) {
    
            const rows:VAExcelExportRow[] = dataObject.map((item: VAExcelExportsHistory.Dto) => ({
                Id: item.id,
                CreatedDateUTC: TableDateHelper(item.createdDateUTC) ?? "",
                CreatedBy: item.createdBy,
                FileName: item.fileName,
                Status: item.statusDescription,
                ScorecardName: item.scorecardName,
                Actions: item.status !== VAExcelExportStatus.InProgress ? [
                    item.status === VAExcelExportStatus.Completed ?
                    {
                        label: 'Download',
                        action: handleDownload,
                        key: item.id,
                        icon: { color: 'info', name: 'download' },
                    } : 
                    {
                        label: 'Export failed. Please contact support.',
                        action: () => { },
                        key: item.id,
                        icon: { color: 'warning', name: 'info' },
                    } 
                ] : []
            }));
    
            return { columns: columns, rows: rows };
    
        } else {
            return { columns: columns, rows: [] };
        }
    }

    const vaExcelExportsHistoryTableData = VAExcelExportsHistoryTableMapper(historyData, handleDownload);
    return (
        <Grid item xs={12} mt={2}>
            <DataTable
                key={'VAExcelExportsHistoryTable'}
                table={vaExcelExportsHistoryTableData}
                entriesPerPage={entriesPerPage}
                pageNumber={pageNumber}
                filteredItemsCount={filteredItemsCount}
                totalItemsCount={totalItemsCount}
                entriesPerPageChanged={entriesPerPageChanged}
                pageNumberChanged={pageNumberChanged}
                isLoading={isLoading}
                canSearch
                searchValueChanged={searchValueChanged}
            ></DataTable>
        </Grid>
    )

}